/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-useless-fragment */
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  Theme,
  Tooltip,
  TooltipProps,
  Typography,
  useMediaQuery,
  tooltipClasses,
  Grid
} from '@mui/material';
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CaseDetails,
  CaseNote,
  EconsultManagedSpecialtyProgram,
  EconsultManagedSpecialtyProgramRegion,
  EconsultRestrictionNoteSection,
  EconsultUnavailabilityDate,
  EconsultUtils,
  NameUtils,
  NoteActionTypes,
  NoteStatusCodes,
  NoteStatusReasons,
  NoteTypes,
  ProgramProfile,
  ProgramSpecialty,
  UserDTO
} from '@oh-vcp/components-common';
import { useNavigate, useParams } from 'react-router-dom';
import {
  RedirectService,
  RequiredFieldTypography,
  RequiredFieldTypographyWithInfo,
  useNotifierContext,
  EconsultActionButton
} from '@oh-vcp/components-ui';
import {
  isForwardingToSameManagedProgram,
  isForwardingToSameRecipient
} from '@oh-vcp/components-common/src/utils/econsultCaseUtils';
import { useQueryClient } from 'react-query';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import CaseService from '../services/caseService';
import { useAuth } from '../auth/AuthContext';
import EconsultUserService from '../services/econsultUserService';
import useAssignerPrograms from '../services/useAssignerPrograms';
import useSpecialtyLookup from '../services/useSpecialtyLookup';
import useSubSpecialtyLookup from '../services/useSubSpecialtyLookup';
import useManagedSpecialtyProgramLookup from '../services/useManagedSpecialtyProgramLookup';
import useProgramLookup from '../services/useProgramLookup';
import EconsultRestrictionNote from '../econsultRestrictionNote/EconsultRestrictionNote';
import { EconsultSpecialistSearchRef } from '../econsultSpecialistSearch/EconsultSpecialistSearch';
import EconsultOutOfOfficeDialog from '../econsultOutOfOfficeDialog/EconsultOutOfOfficeDialog';
import EconsultRedirectSpecialistSearch from '../econsultRedirectSpecialistSearch/EconsultRedirectSpecialistSearch';
import { useEconsultTabContext } from '../econsultTabContext/EconsultTabContext';
import EconsultRedirectDialog from '../econsultRedirectDialog/EconsultRedirectDialog';
import useCaseDetails from '../services/useCaseDetails';

const SpecialtySelect = styled(Select<string>)({
  minWidth: 200,
  height: 40
});

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9'
  }
}));

const EconsultCaseRedirect = () => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { user } = useAuth();
  const { providerType, statusType } = useParams();
  const queryClient = useQueryClient();
  const { caseDetails, setCaseDetails, userRole } = useEconsultCaseDetailsContext();
  const { setSelectedTab } = useEconsultTabContext();
  const [selectedProgramId, setSelectedProgamId] = useState<number | undefined>(undefined);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const navigate = useNavigate();
  const { addNotification, removeAllErrorNotifications } = useNotifierContext();
  const { data: assignerPrograms } = useAssignerPrograms();
  const {
    data: managedProgram,
    isFetching: isProgramLookupFetching,
    isError: isProgramLookupError
  } = useProgramLookup(selectedProgramId);
  const unSelectedOptFontColor = 'base.grey4';
  const selectedOptFontColor = 'primary.main';
  const [isRedirectDialogOpen, setIsRedirectDialogOpen] = useState<boolean>(false);
  const [isRegionSelectShown, setIsRegionSelectShown] = useState<boolean>(false);
  const [selectedRegionId, setSelectedRegionId] = useState<number | undefined>(undefined);
  const [defaultRegionName, setDefaultRegionName] = useState<string | undefined>(undefined);
  const [programType, setProgramType] = useState<string | undefined>();
  const [disableActionButtons, setDisableActionButtons] = useState<boolean>(false);
  const [selectedConsultant, setSelectedConsultant] = useState<
    UserDTO | EconsultManagedSpecialtyProgram | null
  >(null);
  const [programRestrictions, setProgramRestrictions] = useState<
    EconsultRestrictionNoteSection[] | []
  >([]);
  const [specialtyRestriction, setSpecialtyRestrictions] = useState<
    EconsultRestrictionNoteSection[] | []
  >([]);
  const [outOfOfficeConsultant, setOutOfOfficeConsultant] = useState<UserDTO | undefined>(
    undefined
  );
  const [consultOutOfOfficeDate, setConsultOutOfOfficeDate] = useState<
    EconsultUnavailabilityDate | null | undefined
  >(undefined);
  const specialistClearRef = useRef<EconsultSpecialistSearchRef>(null);
  const [options, setOptions] = useState<(UserDTO | EconsultManagedSpecialtyProgram)[]>([]);
  const [caseSpecialty, setCaseSpecialty] = useState<ProgramSpecialty | undefined>();
  const [regionList, setRegionList] = useState<EconsultManagedSpecialtyProgramRegion[] | undefined>(
    undefined
  );
  const [caseSubSpecialty, setCaseSubSpecialty] = useState<ProgramSpecialty | undefined>();
  const [isRecipientError, setIsRecipientError] = useState<boolean>(false);
  const { data: programSpecialties } = useSpecialtyLookup(caseDetails?.referrerId);
  const { data: programSubSpecialties } = useSubSpecialtyLookup(
    caseDetails?.referrerId,
    caseSpecialty?.code
  );
  const {
    data: managedPrograms,
    isFetching: isManagedSpecialtyFetching,
    isError: isManagedSpecialtyError
  } = useManagedSpecialtyProgramLookup(
    caseDetails?.referrerId,
    caseSpecialty?.code,
    caseSubSpecialty?.subCode
  );
  const {
    data: originalCasePrograms,
    isFetching: isOriginalCaseSpecialtyFetching,
    isError: isOriginalCaseSpecialtyError
  } = useManagedSpecialtyProgramLookup(
    caseDetails?.referrerId,
    caseDetails?.specialty?.code,
    caseDetails?.subSpecialty?.subCode
  );
  const { data: participantProgram } = useProgramLookup(
    selectedConsultant && 'programName' in selectedConsultant
      ? selectedConsultant.programId
      : undefined
  );

  useEffect(() => {
    if (isManagedSpecialtyError || isProgramLookupError || isOriginalCaseSpecialtyError) {
      setDisableActionButtons(false);
    } else if (
      isManagedSpecialtyFetching ||
      isProgramLookupFetching ||
      isOriginalCaseSpecialtyFetching
    ) {
      setDisableActionButtons(true);
    } else {
      setDisableActionButtons(false);
    }
  }, [
    isOriginalCaseSpecialtyError,
    isOriginalCaseSpecialtyFetching,
    isManagedSpecialtyFetching,
    isProgramLookupFetching,
    isManagedSpecialtyError,
    isProgramLookupError,
    addNotification,
    setCaseDetails,
    caseDetails
  ]);

  useEffect(() => {
    const regions = managedPrograms?.map((program) => {
      return program.region;
    });
    setRegionList(regions);
    const region = regions?.find((r) => !isForwardingToSameManagedProgram(caseDetails, r));
    if (region) {
      setSelectedRegionId(region.id);
      setDefaultRegionName(region.name);
    }
  }, [managedPrograms]);

  useEffect(() => {
    const p = managedPrograms?.find((prog) => {
      return prog.region.id === selectedRegionId;
    });
    setSelectedProgamId(p?.programId);
  }, [selectedRegionId, managedPrograms]);

  const updateProgramRestrictions = (programProfile: ProgramProfile | undefined) => {
    if (!programProfile) return;
    const restrictions: EconsultRestrictionNoteSection[] = [];
    const econsultRestrictions: EconsultRestrictionNoteSection = {
      title: 'eConsult Case Conditions',
      properties: []
    };
    const aboutGroupRestrictions: EconsultRestrictionNoteSection = {
      title: 'About the Group',
      properties: [
        {
          name: '',
          value: programProfile.programDescription
        }
      ]
    };
    if (programProfile.econCondRegionText) {
      econsultRestrictions.properties.push({
        name: 'Only accepted from LHINs/Regions',
        value: programProfile.econCondRegionText
      });
    }
    if (programProfile.econCondition) {
      econsultRestrictions.properties.push({
        name: 'Other conditions',
        value: programProfile.econCondition
      });
    }

    if (econsultRestrictions && econsultRestrictions.properties.length > 0) {
      restrictions.push(econsultRestrictions);
    }
    if (aboutGroupRestrictions && aboutGroupRestrictions.properties.length > 0) {
      restrictions.push(aboutGroupRestrictions);
    }

    setProgramRestrictions(restrictions);
  };

  useEffect(() => {
    updateProgramRestrictions(managedProgram);
  }, [managedProgram]);

  useEffect(() => {
    updateProgramRestrictions(participantProgram);
  }, [participantProgram]);

  const isUserDTO = (obj: unknown): obj is UserDTO => {
    return typeof obj === 'object' && obj !== null && ('userid' in obj || 'userId' in obj);
  };

  useEffect(() => {
    const restrictions: EconsultRestrictionNoteSection[] = [];
    if (!isUserDTO(selectedConsultant)) return;
    if (selectedConsultant?.specializedAreaOfPractice) {
      restrictions.push({
        title: 'Telemedicine Service Details',
        properties: [
          {
            name: '',
            value: selectedConsultant.specializedAreaOfPractice
          }
        ]
      });
    }
    if (
      selectedConsultant?.econsult &&
      (selectedConsultant.econsult.catchmentArea || selectedConsultant.econsult.otherConditions)
    ) {
      const econsultRestriction: EconsultRestrictionNoteSection = {
        title: 'eConsult Case Conditions',
        properties: []
      };
      if (selectedConsultant.econsult && selectedConsultant.econsult.catchmentArea) {
        econsultRestriction.properties.push({
          name: 'Only accepted from LHINs/Regions',
          value: selectedConsultant.econsult.catchmentArea
        });
      }
      if (selectedConsultant.econsult && selectedConsultant.econsult.otherConditions) {
        econsultRestriction.properties.push({
          name: 'Other conditions',
          value: selectedConsultant.econsult.otherConditions
        });
      }
      if (econsultRestriction.properties.length > 0) {
        restrictions.push(econsultRestriction);
      }
    }
    if (selectedConsultant.patientEligibility) {
      restrictions.push({
        title: 'Patient Eligibility',
        properties: [
          {
            name: '',
            value: selectedConsultant.patientEligibility
          }
        ]
      });
    }
    setSpecialtyRestrictions(restrictions);
  }, [selectedConsultant]);

  useEffect(() => {
    setCaseSpecialty(undefined);
    setCaseSpecialty(undefined);
    setSelectedConsultant(null);
    setOptions([]);
  }, [programType]);

  const isAssignerRedirect = useMemo(() => {
    if (!caseDetails) return false;
    return (
      EconsultUserService.isDepartmentalOrTriageAssignerAction(
        caseDetails,
        userRole,
        assignerPrograms
      ) || EconsultUserService.isTriageRedirectConsultAction(caseDetails, userRole)
    );
  }, [caseDetails, userRole, assignerPrograms]);

  const handleSearchRecipientInputChange = async (inputValue: string) => {
    if (inputValue.length > 1) {
      const specialistsAndGroups = await CaseService.fetchCaseSpecialistsAndGroups(inputValue);
      setOptions(specialistsAndGroups);
    } else {
      setOptions([]);
    }
  };

  const updateConsultant = (consultant: UserDTO | EconsultManagedSpecialtyProgram | null) => {
    setSelectedConsultant(consultant);
  };
  const handleConsultantChanged = (
    consultant: UserDTO | EconsultManagedSpecialtyProgram | null
  ) => {
    if (!consultant) {
      setOptions([]);
    }
    if (isForwardingToSameRecipient(consultant, caseDetails)) {
      setIsRecipientError(true);
      setOptions([]);
      setTimeout(() => {
        specialistClearRef.current?.clear();
      });
      return;
    }

    setIsRecipientError(false);

    if (isUserDTO(consultant)) {
      const consultantUser = consultant as UserDTO;
      if (consultantUser?.unavailability) {
        const unavailableDate = EconsultUtils.getOutOfOfficeDateInAWeek(
          consultantUser.unavailability
        );
        if (unavailableDate) {
          setOutOfOfficeConsultant(consultant);
          setConsultOutOfOfficeDate(unavailableDate);
          return;
        }
      }
    }
    updateConsultant(consultant);
  };
  const handleOutOfOfficeContinue = () => {
    if (outOfOfficeConsultant) {
      updateConsultant(outOfOfficeConsultant);
      setOutOfOfficeConsultant(undefined);
      setConsultOutOfOfficeDate(undefined);
    }
  };
  const handleOutOfOfficeCancel = () => {
    setOutOfOfficeConsultant(undefined);
    setConsultOutOfOfficeDate(undefined);
    specialistClearRef.current?.clear();
    updateConsultant(null);
  };

  const getNoteToRedirect = async (userid: number): Promise<CaseNote> => {
    let draftNote = caseDetails?.notes?.find(
      (n) => n.statusCd === NoteStatusCodes.draft && n.noteTypeCd === NoteTypes.referrer
    );
    if (draftNote) {
      draftNote.statusCd = NoteStatusCodes.active;
      draftNote.actionTypeCd = NoteActionTypes.forward;
    } else {
      draftNote = {
        ownerId: userid,
        noteTypeCd: NoteTypes.referrer,
        actionTypeCd: NoteActionTypes.forward,
        caseId: caseDetails?.caseId,
        statusCd: NoteStatusCodes.active
      };
    }

    return CaseService.upsertCaseNote(draftNote);
  };

  const handleAssignerRedirect = async () => {
    if (!user) {
      return;
    }
    setIsRedirecting(true);
    const { userid, firstName, lastName } = user;
    const updatedCaseDetails = {
      ...caseDetails,
      statusCd: NoteStatusCodes.closed,
      statusReason: NoteStatusReasons.cancelled,
      participant: {
        ...caseDetails?.participant,
        statusCd: NoteStatusCodes.closed,
        ...(caseDetails?.participant?.triageStatusCd && { triageStatusCd: NoteStatusCodes.closed })
      },
      notes: [
        {
          caseId: caseDetails?.caseId,
          noteTypeCd: NoteTypes.triageAdmin,
          actionTypeCd: NoteActionTypes.addNote,
          statusCd: NoteStatusCodes.active,
          noteContent: '',
          parameter1:
            programType === 'DIRECT'
              ? selectedConsultant && 'programName' in selectedConsultant
                ? selectedConsultant.programName
                : NameUtils.makeName(
                    (selectedConsultant as UserDTO).lastName || '',
                    (selectedConsultant as UserDTO).firstName,
                    null,
                    null,
                    null,
                    null
                  )
              : managedProgram?.programName,
          ownerId: 999900019475
        },
        {
          noteTypeCd: NoteTypes.triageAdmin,
          actionTypeCd: NoteActionTypes.forward,
          statusCd: NoteStatusCodes.active,
          caseId: caseDetails?.caseId,
          ownerId: 999900019475
        }
      ]
    };
    if (userid && firstName && lastName) {
      const firstNote: CaseNote | undefined = caseDetails?.notes?.filter(
        (n) => !n.actionTypeCd || n.actionTypeCd === NoteActionTypes.send
      )[0];
      const newCaseNote: CaseNote = {};
      if (firstNote) {
        newCaseNote.statusCd = NoteStatusCodes.active;
        newCaseNote.noteContent = firstNote.noteContent;
        newCaseNote.noteTypeCd = NoteTypes.referrer;
        newCaseNote.attachments = firstNote.attachments;
        newCaseNote.actionTypeCd = NoteActionTypes.send;
      }
      const { patientId, ...patientWithoutId } = caseDetails?.patient || {};
      const redirectedCaseDetails: CaseDetails = {
        statusCd: NoteStatusCodes.active,
        originalCaseId: caseDetails?.caseId,
        patient: patientWithoutId,
        patientConsent: caseDetails?.patientConsent,
        referrer: {
          userId: caseDetails?.referrerId
        },
        notes: newCaseNote ? [newCaseNote] : [],
        programType,
        specialty: caseSpecialty,
        subSpecialty: caseSubSpecialty,
        participant:
          programType === 'DIRECT'
            ? {
                ...(selectedConsultant && 'programName' in selectedConsultant
                  ? { program: selectedConsultant }
                  : { consultant: selectedConsultant }),
                statusCd: NoteStatusCodes.active
              }
            : {
                program: managedProgram,
                triageStatusCd: NoteStatusCodes.active,
                statusCd: NoteStatusCodes.active
              }
      };
      removeAllErrorNotifications();
      const response = await CaseService.redirectCaseDetails([
        updatedCaseDetails,
        redirectedCaseDetails
      ]);
      setIsRedirecting(false);
      if (response) {
        if (providerType === 'search') {
          queryClient.invalidateQueries(['caseSearch']);
        } else {
          queryClient.invalidateQueries(['caseResults']);
        }
        const urlToNavigate = CaseService.getReturnNavigationUrl(providerType, statusType, user);
        navigate(urlToNavigate.toLowerCase());
        addNotification({
          type: 'success',
          message: t('Case.consult.assignerRedirectSuccess')
        });
      }
    }
  };

  const handleRedirect = async () => {
    if (!user) {
      return;
    }
    setIsRedirecting(true);
    const { userid, firstName, lastName } = user;
    const noteToRedirect = await getNoteToRedirect(user?.userId as number);
    const updatedCaseDetails = {
      ...caseDetails,
      statusCd: NoteStatusCodes.closed,
      statusReason: NoteStatusReasons.cancelled,
      participant: {
        ...caseDetails?.participant,
        statusCd: NoteStatusCodes.closed,
        ...(caseDetails?.participant?.triageStatusCd && { triageStatusCd: NoteStatusCodes.closed })
      },
      notes: [noteToRedirect]
    };
    setCaseDetails(updatedCaseDetails);
    await CaseService.upsertCaseDetails(updatedCaseDetails);
    if (userid && firstName && lastName) {
      const firstNote: CaseNote | undefined = caseDetails?.notes?.filter(
        (n) => !n.actionTypeCd || n.actionTypeCd === NoteActionTypes.send
      )[0];
      const newCaseNote: CaseNote = {};
      if (firstNote) {
        newCaseNote.statusCd = NoteStatusCodes.draft;
        newCaseNote.noteContent = firstNote.noteContent;
        newCaseNote.noteTypeCd = NoteTypes.referrer;
        newCaseNote.attachments = firstNote.attachments;
      }
      removeAllErrorNotifications();
      const { patientId, ...patientWithoutId } = caseDetails?.patient || {};
      const response = await CaseService.upsertCaseDetails({
        statusCd: NoteActionTypes.draft,
        originalCaseId: caseDetails?.caseId,
        patient: patientWithoutId,
        patientConsent: caseDetails?.patientConsent,
        referrer: {
          userId: caseDetails?.referrer?.userId
        },
        notes: newCaseNote ? [newCaseNote] : []
      });
      setIsRedirecting(false);
      if (response?.data?.caseId) {
        const urlToNavigate = `/case/${NoteTypes.referrer}/${NoteActionTypes.draft}/${response.data.caseId}`;
        navigate(urlToNavigate.toLowerCase());
        addNotification({
          type: 'success',
          message: t('Case.consult.redirectSuccess')
        });
      }
    }
  };
  const handleProgramTypeChanged = (event: ChangeEvent<HTMLInputElement>, value: string) => {
    setProgramType(value);
    setIsRegionSelectShown(false);
    specialistClearRef?.current?.clear();
  };

  const isSubSpecialtyDisabled = (code: string | undefined, subCode: string | undefined) => {
    if (
      originalCasePrograms &&
      originalCasePrograms.length === 1 &&
      caseDetails?.participant &&
      caseDetails?.participant.program
    ) {
      if (
        caseDetails?.specialty?.code === code &&
        caseDetails?.subSpecialty?.subCode === subCode &&
        originalCasePrograms[0].programId === caseDetails.participant.program.programId
      ) {
        return true;
      }
    }
    return false;
  };

  const handleSpecialtyChanged = (event: SelectChangeEvent) => {
    if (!programSpecialties) return;
    const specialtyCode = event.target.value;
    const selectedSpecialty = programSpecialties.find((s) => s.code === specialtyCode);
    setCaseSpecialty(selectedSpecialty);
    setIsRegionSelectShown(false);
    setCaseSubSpecialty(undefined);
  };
  const handleSubSpecialtyChanged = (event: SelectChangeEvent) => {
    if (!programSubSpecialties) {
      return;
    }
    const subSpecialtyCode = event.target.value;
    const selectedSubSpecialty = programSubSpecialties.find((s) => s.subCode === subSpecialtyCode);
    if (
      selectedSubSpecialty &&
      isSubSpecialtyDisabled(selectedSubSpecialty.code, selectedSubSpecialty.subCode)
    ) {
      return;
    }
    setCaseSubSpecialty(selectedSubSpecialty);
    setIsRegionSelectShown(false);
  };
  const handleViewProgram = () => {
    if (selectedProgramId) {
      RedirectService.redirectToProgramProfile(selectedProgramId.toString());
    }
  };

  const handleViewPeopleProfile = () => {
    if (selectedConsultant && isUserDTO(selectedConsultant)) {
      RedirectService.redirectToDirectoryPeopleProfile(selectedConsultant.contactId?.toString());
    } else if (selectedConsultant?.programId) {
      RedirectService.redirectToProgramProfile(selectedConsultant.programId.toString());
    }
  };

  const isRedirectDisabled = () => {
    const ready =
      (programType && programType === 'MANAGED_SPECIALTY_GROUP' && managedProgram) ||
      (programType === 'DIRECT' && selectedConsultant);
    return !ready || isRedirecting;
  };

  const handleRedirectCancel = () => {
    setSelectedTab(undefined);
  };

  const handleRedirectContinue = () => {
    handleAssignerRedirect();
    setIsRedirectDialogOpen(false);
  };

  const isRegionDisabled = (region: EconsultManagedSpecialtyProgramRegion) => {
    if (programType === 'MANAGED_SPECIALTY_GROUP') {
      const program = managedPrograms?.find(
        (p: EconsultManagedSpecialtyProgram) => p.region?.id === region.id
      );
      return program?.programId === caseDetails?.participant?.program?.programId;
    }
    return programType === 'DIRECT' && isForwardingToSameManagedProgram(caseDetails, region);
  };

  const getSubSpecialtyName = (subSpecialty: ProgramSpecialty) => {
    if (isSubSpecialtyDisabled(subSpecialty.code, subSpecialty.subCode)) {
      return `${subSpecialty.description} - unavailable Region`;
    }
    return subSpecialty.description;
  };

  const displaySubSpecialties = useMemo(() => {
    return EconsultUtils.sortProgramSpecialties(programSubSpecialties).map((specialty) => ({
      ...specialty,
      displayName: getSubSpecialtyName(specialty),
      isDisabled: isSubSpecialtyDisabled(specialty.code, specialty.subCode)
    }));
  }, [programSpecialties, programSubSpecialties, originalCasePrograms]);

  if (!caseDetails) {
    return null;
  }

  return (
    <>
      <Box>
        {isAssignerRedirect ? (
          <>
            <Typography component="p" variant="body2" color="base.grey4">
              {t('Case.consult.redirectConfirmationAssigner')}
            </Typography>
            <Grid container spacing={0}>
              <Grid item py={1} xs={12} md={2}>
                <HtmlTooltip
                  title={
                    <>
                      <b>BASE Managed Specialty:</b>
                      This option enables you to submit cases to either a regional (where available)
                      or provincial managed specialty group, which is a group of specialists
                      responding to eConsult cases for a given specialty or sub-specialty (e.g.
                      pediatric cardiology). The case is then assigned based on specialist
                      availability.&apos;
                      <br />
                      <br />
                      <b>Specific Provider or Group: </b> This option allows you to submit cases to
                      specific consultants by name or to organizational or regional groups (ex.
                      UHN&apos;s SCOPE group). Cases are sent directly to the consultant or
                      group.&apos;
                    </>
                  }>
                  <RequiredFieldTypographyWithInfo variant="body2" color="base.grey4" pt={1}>
                    {t('Case.detials.model')}
                  </RequiredFieldTypographyWithInfo>
                </HtmlTooltip>
              </Grid>
              <Grid item py={1} xs={12} md={10}>
                <FormControl>
                  <RadioGroup
                    aria-labelledby="demo-form-control-label-placement"
                    name="position"
                    onChange={handleProgramTypeChanged}
                    value={programType}>
                    <FormControlLabel
                      value="MANAGED_SPECIALTY_GROUP"
                      control={
                        <Radio
                          sx={{
                            '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
                              color:
                                programType === 'MANAGED_SPECIALTY_GROUP'
                                  ? selectedOptFontColor
                                  : unSelectedOptFontColor
                            },
                            '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
                              color:
                                programType === 'MANAGED_SPECIALTY_GROUP'
                                  ? selectedOptFontColor
                                  : unSelectedOptFontColor
                            }
                          }}
                        />
                      }
                      label="BASE Managed Specialty"
                      labelPlacement="end"
                      sx={{
                        color:
                          programType === 'MANAGED_SPECIALTY_GROUP'
                            ? selectedOptFontColor
                            : unSelectedOptFontColor,
                        fontSize: '0.875rem',
                        '& .MuiTypography-root': {
                          fontSize: '0.875rem'
                        }
                      }}
                    />
                    <FormControlLabel
                      value="DIRECT"
                      control={
                        <Radio
                          sx={{
                            '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
                              color:
                                programType === 'DIRECT'
                                  ? selectedOptFontColor
                                  : unSelectedOptFontColor
                            },
                            '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
                              color:
                                programType === 'DIRECT'
                                  ? selectedOptFontColor
                                  : unSelectedOptFontColor
                            }
                          }}
                        />
                      }
                      label="Specific Provider or Group"
                      labelPlacement="end"
                      sx={{
                        color:
                          programType === 'DIRECT' ? selectedOptFontColor : unSelectedOptFontColor,
                        fontSize: '0.875rem',
                        '& .MuiTypography-root': {
                          fontSize: '0.875rem'
                        }
                      }}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              {programType === 'MANAGED_SPECIALTY_GROUP' && (
                <>
                  <Grid item py={1} xs={12} md={2}>
                    <RequiredFieldTypography variant="body2" color="base.grey4" pt={1}>
                      {t('Case.draft.details.view.speciality')}
                    </RequiredFieldTypography>
                  </Grid>
                  <Grid item py={1} xs={12} md={10}>
                    <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
                      <SpecialtySelect
                        onChange={handleSpecialtyChanged}
                        value={caseSpecialty?.code ?? '0'}>
                        <MenuItem
                          value="0"
                          sx={{
                            '&:hover': { color: 'initial' }
                          }}>
                          <Typography variant="body2">{t('Case.draft.selectCategory')}</Typography>
                        </MenuItem>
                        {EconsultUtils.sortProgramSpecialties(programSpecialties)?.map(
                          (specialty) => (
                            <MenuItem
                              key={specialty.code}
                              value={specialty.code}
                              sx={{
                                '&.Mui-selected:hover': { color: 'black' }
                              }}>
                              <Typography variant="body2">{specialty.description}</Typography>
                            </MenuItem>
                          )
                        )}
                      </SpecialtySelect>
                      {programSubSpecialties && (
                        <SpecialtySelect
                          onChange={handleSubSpecialtyChanged}
                          value={caseSubSpecialty?.subCode ?? '0'}>
                          <MenuItem
                            value="0"
                            sx={{
                              '&:hover': { color: 'initial' }
                            }}>
                            <Typography variant="body2">{t('Case.draft.selectOption')}</Typography>
                          </MenuItem>
                          {displaySubSpecialties.map((specialty, index) => (
                            <MenuItem
                              disabled={specialty.isDisabled}
                              key={index}
                              value={specialty.subCode}
                              sx={{
                                '&.Mui-selected:hover': { color: 'black' }
                              }}>
                              <Typography variant="body2">{specialty.displayName}</Typography>
                            </MenuItem>
                          ))}
                        </SpecialtySelect>
                      )}
                    </Stack>
                  </Grid>
                  {regionList && regionList.length > 0 && (
                    <>
                      <Grid item xs={12} md={2}>
                        <HtmlTooltip title={<>{t('Case.draft.details.view.region.tool.tip')}</>}>
                          <RequiredFieldTypographyWithInfo
                            variant="body2"
                            color="base.grey4"
                            pt={2}>
                            {t('Case.draft.details.view.region')}
                          </RequiredFieldTypographyWithInfo>
                        </HtmlTooltip>
                      </Grid>
                      <Grid item xs={12} md={10}>
                        <Stack direction="row" alignItems="center">
                          <>
                            {!isRegionSelectShown ? (
                              <>
                                <Typography variant="body2">{defaultRegionName}</Typography>
                                {regionList.length > 1 && (
                                  <Button
                                    variant="text"
                                    sx={{ ml: 3 }}
                                    onClick={() => setIsRegionSelectShown(true)}>
                                    Change
                                  </Button>
                                )}
                              </>
                            ) : (
                              <Select
                                sx={{ minWidth: 200, height: 40 }}
                                value={selectedRegionId}
                                onChange={(e) =>
                                  setSelectedRegionId(e.target.value as number | undefined)
                                }>
                                {regionList.map((region) => (
                                  <MenuItem value={region.id} disabled={isRegionDisabled(region)}>
                                    <Typography variant="body2">{region.name}</Typography>
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          </>
                        </Stack>
                        {(managedProgram?.econCondRegionText || managedProgram?.econCondition) && (
                          <EconsultRestrictionNote
                            note={t('Case.consult.restrictionsNote')}
                            properties={programRestrictions}
                          />
                        )}
                        {regionList && regionList.length > 0 && (
                          <Button variant="text" sx={{ pl: 0 }} onClick={handleViewProgram}>
                            {t('Case.consult.viewDirectoryProfile')}
                          </Button>
                        )}
                      </Grid>
                    </>
                  )}
                </>
              )}
              {programType === 'DIRECT' && (
                <>
                  <Grid item xs={12} md={2}>
                    <RequiredFieldTypography variant="body2" color="base.grey4" pt={2}>
                      {t('Case.draft.details.view.recipient')}
                    </RequiredFieldTypography>
                  </Grid>
                  <Grid item xs={12} md={10}>
                    <EconsultRedirectSpecialistSearch
                      options={options}
                      onInputChange={handleSearchRecipientInputChange}
                      onSelectionChange={handleConsultantChanged}
                      ref={specialistClearRef}
                      searchValue={selectedConsultant}
                    />
                    {isRecipientError && (
                      <Typography variant="body2" color="red">
                        {t('Case.draft.recipientError')}
                      </Typography>
                    )}
                    {selectedConsultant &&
                      isUserDTO(selectedConsultant) &&
                      ((selectedConsultant.econsult &&
                        (selectedConsultant.econsult.catchmentArea ||
                          selectedConsultant.econsult.otherConditions)) ||
                        selectedConsultant.patientEligibility ||
                        selectedConsultant.specializedAreaOfPractice) && (
                        <>
                          <EconsultRestrictionNote
                            note={t('Case.consult.specialistRestrictionsNote')}
                            properties={specialtyRestriction}
                          />
                        </>
                      )}
                    {selectedConsultant &&
                      'programName' in selectedConsultant &&
                      (selectedConsultant.econCondRegion ||
                        selectedConsultant.programDescription ||
                        selectedConsultant.econCondition) && (
                        <>
                          <EconsultRestrictionNote
                            note={t('Case.consult.restrictionsNote')}
                            properties={programRestrictions}
                          />
                        </>
                      )}
                    {selectedConsultant && (
                      <Button variant="text" sx={{ pl: 0 }} onClick={handleViewPeopleProfile}>
                        {t('Case.consult.viewDirectoryProfile')}
                      </Button>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
            <Stack direction="row">
              <EconsultActionButton
                sx={{ mt: 1, mb: 3 }}
                variant="contained"
                onClick={() => setIsRedirectDialogOpen(true)}
                disabled={isRedirectDisabled() || disableActionButtons}>
                {t('Case.consult.redirect')}
              </EconsultActionButton>
              <EconsultActionButton
                sx={{ mt: 1, mb: 3, ml: 2 }}
                variant="contained"
                disabled={isRedirecting || disableActionButtons}
                onClick={handleRedirectCancel}>
                Cancel
              </EconsultActionButton>
            </Stack>
          </>
        ) : (
          <>
            <Typography component="p">{t('Case.consult.redirectConfirmation')}</Typography>
            <Typography variant="body4" component="p" sx={{ mt: 1 }}>
              {t('Case.consult.redirectSecondaryConfirmation')}
            </Typography>
            <Typography variant="body4" component="p" sx={{ mt: 1 }}>
              {t('Case.consult.redirectCurrentCaseWillBeCancelled')}
            </Typography>
            <EconsultActionButton
              sx={{ mt: 1, mb: 3 }}
              variant="contained"
              onClick={handleRedirect}
              disabled={isRedirecting || disableActionButtons}>
              {t('Case.consult.redirect')}
            </EconsultActionButton>
          </>
        )}
      </Box>
      <EconsultOutOfOfficeDialog
        isDialogOpen={Boolean(outOfOfficeConsultant)}
        startDate={consultOutOfOfficeDate?.startDate}
        endDate={consultOutOfOfficeDate?.endDate}
        onCancelClicked={handleOutOfOfficeCancel}
        onContinueClicked={handleOutOfOfficeContinue}
        userName={NameUtils.makeName(
          outOfOfficeConsultant?.lastName || '',
          outOfOfficeConsultant?.firstName,
          null,
          null,
          null,
          null,
          null,
          null
        )}
      />
      <EconsultRedirectDialog
        isDialogOpen={isRedirectDialogOpen}
        onContine={handleRedirectContinue}
        onCancel={() => setIsRedirectDialogOpen(false)}
      />
    </>
  );
};

export default EconsultCaseRedirect;
